import {
  doc,
  DocumentData,
  DocumentSnapshot,
  FirestoreDataConverter,
  getDoc,
  setDoc,
} from 'firebase/firestore';

import {
  CareerRoadMapAnswers,
  CareerRoadMapDto,
  careerRoadMapIntitalAnswers as rawCareerRoadMapInitialAnswers,
} from '../../redux/slice/career-roadmap/career-roadmap-type';
import logger from '../logger';

import { getFirestoreDatabase } from './configure';
import { DatabaseTable } from './database.types';
import { getIdForFormRecord } from './database.utils';

const database: DatabaseTable = DatabaseTable.CAREER_ROADMAP_ASSESSMENT;

/**
 * Replace any undefined values with null so Firestore accepts them.
 */
function sanitizeFirestore<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj, (_key, value) => (value === undefined ? null : value)));
}

// Ensure initial answers never contain undefined
export const careerRoadMapIntitalAnswers = sanitizeFirestore(rawCareerRoadMapInitialAnswers);

const careerRoadMapConverter: FirestoreDataConverter<CareerRoadMapAnswers> = {
  toFirestore: (data: CareerRoadMapAnswers): DocumentData => data,
  fromFirestore: (snap: DocumentSnapshot): CareerRoadMapAnswers =>
    snap.data() as CareerRoadMapAnswers,
};

export async function getCareerRoadMapAssessmentValues(): Promise<CareerRoadMapAnswers> {
  try {
    const recordId: string = await getIdForFormRecord();
    const ref = doc(getFirestoreDatabase(), database, recordId).withConverter(
      careerRoadMapConverter,
    );
    const result = await getDoc(ref);

    return result.data() ?? ({} as CareerRoadMapAnswers);
  } catch (e) {
    logger.error('Failed to retrieve user course question values.', e);
    throw e;
  }
}
async function findCareerRoadMapAssessmentRecord(
  recordId: string,
): Promise<CareerRoadMapAnswers | undefined> {
  try {
    const result: DocumentSnapshot<Partial<CareerRoadMapAnswers>> = await getDoc(
      doc(getFirestoreDatabase(), database, recordId),
    );
    return (result.data() as CareerRoadMapAnswers) ?? undefined;
  } catch (e) {
    logger.error('Failed to retrieve career-roadmap-assessment values.', e);
    throw e;
  }
}

async function updateCareerRoadMapAssessment(
  field: keyof CareerRoadMapAnswers,
  value: CareerRoadMapDto,
): Promise<void> {
  const recordId: string = await getIdForFormRecord();

  await setDoc(
    doc(getFirestoreDatabase(), database, recordId),
    { [field]: sanitizeFirestore(value) }, // ✅ update only this field
    { merge: true },
  );
}

export async function upsertCareerRoadMapAssessment(
  dto?: Partial<CareerRoadMapAnswers>,
): Promise<CareerRoadMapAnswers> {
  logger.debug('Upserting career roadmap assessment.');
  try {
    const recordId: string = await getIdForFormRecord();
    let assessment = await findCareerRoadMapAssessmentRecord(recordId);

    if (dto && Object.keys(dto).length > 0) {
      // 🔑 only update the fields provided in dto
      for (const [field, value] of Object.entries(dto)) {
        await updateCareerRoadMapAssessment(
          field as keyof CareerRoadMapAnswers,
          value as CareerRoadMapDto,
        );
      }
      assessment = await findCareerRoadMapAssessmentRecord(recordId);
    }

    if (!assessment) {
      // 🔑 initialize the record if not found
      const initial: CareerRoadMapAnswers = {
        userId: recordId,
        isCareerRoadMapAssessmentComplete: false,
        region_location: careerRoadMapIntitalAnswers,
        motivation_vector: careerRoadMapIntitalAnswers,
        workstyle_vector: careerRoadMapIntitalAnswers,
        digital_literacy: careerRoadMapIntitalAnswers,
        tool_exposure: careerRoadMapIntitalAnswers,
        sector_interest: careerRoadMapIntitalAnswers,
        scenario_people: careerRoadMapIntitalAnswers,
        scenario_ops: careerRoadMapIntitalAnswers,
        role_alignment: careerRoadMapIntitalAnswers,
        problem_style: careerRoadMapIntitalAnswers,
        detail_orientation: careerRoadMapIntitalAnswers,
        physicality_score: careerRoadMapIntitalAnswers,
        facility_environment: careerRoadMapIntitalAnswers,
        customer_interaction: careerRoadMapIntitalAnswers,
        structure_preference: careerRoadMapIntitalAnswers,
        availability: careerRoadMapIntitalAnswers,
        certs_vector: careerRoadMapIntitalAnswers,
        green_orientation: careerRoadMapIntitalAnswers,
        structured_context: careerRoadMapIntitalAnswers,
        context_structured_bilingual: careerRoadMapIntitalAnswers,
        context_structured_transport: careerRoadMapIntitalAnswers,
        context_structured_caregiving: careerRoadMapIntitalAnswers,
        context_notes: careerRoadMapIntitalAnswers,
        isRoadmapAiGenerated: false,
      };

      for (const [field, value] of Object.entries(initial)) {
        await updateCareerRoadMapAssessment(
          field as keyof CareerRoadMapAnswers,
          value as CareerRoadMapDto,
        );
      }
      assessment = await findCareerRoadMapAssessmentRecord(recordId);
    }

    if (!assessment) {
      throw new Error('Career roadmap assessment not found.');
    }

    return assessment;
  } catch (e) {
    logger.error('Failed to upsert career roadmap assessment.', e);
    throw e;
  }
}
